import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@radix-ui/themes';
import snapgradeLogo from '../../img/snapgrade-logo.svg';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleLoginClick = () => {
    navigate('/login');
  };

  // Navigation Component (same as landing page)
  const Navigation = () => (
    <nav className="bg-[#F0D143] border-b-0 py-6 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={snapgradeLogo} 
              alt="SnapGrade" 
              className="h-32 w-auto"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <a href="/#product" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              Product
            </a>
            <a href="/#pricing" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              Pricing
            </a>
            <a href="/#faqs" className="text-black font-medium text-lg hover:text-gray-700 transition-colors">
              FAQs
            </a>
          </div>
          
          {/* Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              className="bg-black text-white hover:bg-gray-800 px-8 py-3 rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-black hover:border-gray-800"
              onClick={handleLoginClick}
            >
              Login
            </Button>
            <Button 
              className="bg-white text-black hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold text-base border-2 border-black hover:border-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
              onClick={() => window.open('http://calendly.com/snapgrade', '_blank')}
            >
              Book A Demo
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button variant="ghost" className="text-black">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div className="lg:hidden mt-4 space-y-2">
          <div className="flex flex-col space-y-2">
            <a href="/#product" className="text-black font-medium py-2">Product</a>
            <a href="/#pricing" className="text-black font-medium py-2">Pricing</a>
            <a href="/#faqs" className="text-black font-medium py-2">FAQs</a>
          </div>
          <div className="flex flex-col space-y-2 pt-2 border-t border-black/20">
            <Button 
              className="bg-black text-white hover:bg-gray-800 w-full py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-2 border-black hover:border-gray-800"
              onClick={handleLoginClick}
            >
              Login
            </Button>
            <Button 
              className="bg-white text-black hover:bg-gray-100 w-full py-3 rounded-lg font-semibold border-2 border-black hover:border-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
              onClick={() => window.open('http://calendly.com/snapgrade', '_blank')}
            >
              Book A Demo
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Footer Component (same as landing page)
  const Footer = () => (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-[#F0D143] p-3 rounded-lg inline-block mb-4">
              <img src={snapgradeLogo} alt="SnapGrade" className="h-16 w-auto" />
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 SnapGrade, from STEM@Home Ltd. All rights reserved.
            </p>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Product</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/#product" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="/#pricing" className="hover:text-white transition-colors">Pricing</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Company</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="mailto:francis.jones@snapgrade.ai" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="font-semibold mb-4">Legal</h5>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/eula" className="hover:text-white transition-colors">End User Licence Agreement</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="text-center">
            <h5 className="font-semibold mb-4">Socials</h5>
            <a 
              href="https://www.linkedin.com/company/snapgradeuk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navigation />

      {/* Privacy Policy Content */}
      <section className="bg-[#FCF6DF] py-16 lg:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl lg:text-4xl font-bold text-black mb-4 text-center">
              SnapGrade Customer Privacy Notice
            </h1>
            <p className="text-center text-gray-600 mb-2"><strong>Registered name:</strong> STEM@Home Ltd</p>
            <p className="text-center text-gray-600 mb-8">This privacy notice tells you what to expect us to do with your personal information.</p>
            
            <div className="prose prose-lg max-w-none text-gray-700">
              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">Contact details</h2>
              <p className="mb-6"><strong>Email:</strong> <a href="mailto:francis.jones@snapgrade.ai" className="text-blue-600 hover:text-blue-800 transition-colors">francis.jones@snapgrade.ai</a></p>
              
              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">What information we collect, use, and why</h2>
              <p className="mb-4">We collect or use the following information to provide and improve products and services for clients:</p>
              <ul className="mb-6 pl-6">
                <li>Names and contact details</li>
                <li>Payment details (including card or bank information for transfers and direct debits)</li>
                <li>Usage data (including information about how you interact with and use our website, products and services)</li>
              </ul>
              
              <p className="mb-4">We collect or use the following personal information for the operation of client or customer accounts:</p>
              <ul className="mb-6 pl-6">
                <li>Names and contact details</li>
                <li>Purchase or service history</li>
              </ul>
              
              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">Information Collected</h2>
              <p className="mb-4">When you register for SnapGrade, we collect certain personally identifiable information, which may include:</p>
              <ul className="mb-6 pl-6">
                <li>Name</li>
                <li>Email address</li>
                <li>Educational institution information</li>
                <li>Account login credentials</li>
              </ul>
              
              <p className="mb-4">When you use our Service to scan and submit work for grading, we process:</p>
              <ul className="mb-6 pl-6">
                <li>Images of handwritten or typed work that you upload</li>
                <li>Text extracted from those images</li>
                <li>Grading and feedback data generated by our system</li>
              </ul>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6 rounded">
                <p className="mb-0"><strong>IMPORTANT:</strong> We do not store or retain the images you upload to our Service. Once work is scanned and text is extracted, the image is deleted immediately. We cannot access these images after processing. We only save the extracted text, scores, and feedback for each submission.</p>
              </div>
              
              <p className="mb-4">We also automatically gather certain information about your use of the Platform, such as:</p>
              <ul className="mb-6 pl-6">
                <li>Device information (type, operating system, browser)</li>
                <li>IP address</li>
                <li>Usage patterns and frequency</li>
                <li>Log data</li>
                <li>Cookies and similar technologies</li>
              </ul>
              
              <p className="mb-6">Most browsers can be set to prevent cookies or to notify you when one is placed. However, cookies may be necessary to access some interactive features of our Platform.</p>
              
              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">How We Use Your Information</h2>
              <p className="mb-4">We use your personal information to:</p>
              <ul className="mb-6 pl-6">
                <li>Provide, maintain, and improve the SnapGrade Service</li>
                <li>Process and complete transactions</li>
                <li>Send administrative information, including confirmations, technical notices, updates, and security alerts</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Provide customer service and support</li>
                <li>Monitor and analyze trends, usage, and activities in connection with our Service</li>
                <li>Detect, prevent, and address technical issues</li>
                <li>Comply with legal obligations</li>
              </ul>
              
              <p className="mb-6">We will not share or disclose your information to anyone except as described in this Privacy Policy.</p>
              
              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">Data Processing and Retention</h2>
              <p className="mb-4">When you upload images to our Service:</p>
              <ul className="mb-6 pl-6">
                <li>The image is temporarily stored on our secure servers for immediate processing</li>
                <li>Our system extracts the text content from the image</li>
                <li>The extracted text is analyzed for grading purposes</li>
                <li>Feedback and scores are generated and saved to your account</li>
                <li>The original image is permanently deleted from our servers immediately after processing</li>
              </ul>
              
              <p className="mb-4">We retain only:</p>
              <ul className="mb-6 pl-6">
                <li>Your account information</li>
                <li>The extracted text from submissions</li>
                <li>Scores and feedback for each submission</li>
                <li>Analytics data related to your usage of the Service</li>
              </ul>

              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">How long we keep information</h2>
              <p className="mb-6">This Data Retention Schedule establishes the guidelines for retaining and disposing of data collected and processed by SnapGrade in accordance with our Privacy Policy. This schedule is designed to ensure that we maintain data only as long as necessary for our legitimate business purposes and to comply with applicable laws and regulations.</p>
              
              <div className="overflow-x-auto mb-8">
                <h3 className="text-xl font-semibold text-black text-center mb-4">DATA CATEGORIES AND RETENTION PERIODS</h3>
                <table className="w-full border-collapse border border-gray-300 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Data Type</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Description</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Retention Period</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Retention Justification</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Disposal Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 p-3 font-semibold">User Account Information</td>
                      <td className="border border-gray-300 p-3">Name, email address, educational institution, login credentials</td>
                      <td className="border border-gray-300 p-3">Active period of account + 24 months after account inactivity</td>
                      <td className="border border-gray-300 p-3">Necessary for account management and potential reactivation</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr className="bg-gray-25">
                      <td className="border border-gray-300 p-3 font-semibold">Uploaded Images</td>
                      <td className="border border-gray-300 p-3">Photos of handwritten or typed work submitted for grading</td>
                      <td className="border border-gray-300 p-3">Immediate deletion after text extraction (typically within minutes of upload)</td>
                      <td className="border border-gray-300 p-3">No business need to retain images once text is extracted</td>
                      <td className="border border-gray-300 p-3">Immediate secure deletion</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3 font-semibold">Extracted Text</td>
                      <td className="border border-gray-300 p-3">Text content extracted from uploaded images</td>
                      <td className="border border-gray-300 p-3">24 months from date of submission</td>
                      <td className="border border-gray-300 p-3">Necessary for reference, appeals, and educational progress tracking</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr className="bg-gray-25">
                      <td className="border border-gray-300 p-3 font-semibold">Scores and Feedback</td>
                      <td className="border border-gray-300 p-3">Assessment results and comments generated by the system</td>
                      <td className="border border-gray-300 p-3">36 months from date of submission</td>
                      <td className="border border-gray-300 p-3">Necessary for educational progress tracking and historical performance analysis</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3 font-semibold">User Activity Logs</td>
                      <td className="border border-gray-300 p-3">Records of platform usage including login times, feature usage</td>
                      <td className="border border-gray-300 p-3">12 months from creation</td>
                      <td className="border border-gray-300 p-3">Security monitoring, service improvement, and troubleshooting</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr className="bg-gray-25">
                      <td className="border border-gray-300 p-3 font-semibold">Communication Records</td>
                      <td className="border border-gray-300 p-3">Support tickets, emails, and other communications with users</td>
                      <td className="border border-gray-300 p-3">24 months from last communication</td>
                      <td className="border border-gray-300 p-3">Customer support reference and quality assurance</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3 font-semibold">Payment Information</td>
                      <td className="border border-gray-300 p-3">Transaction records (not full payment details)</td>
                      <td className="border border-gray-300 p-3">7 years from transaction date</td>
                      <td className="border border-gray-300 p-3">Financial record-keeping, refund processing, and tax compliance</td>
                      <td className="border border-gray-300 p-3">Secure deletion from databases</td>
                    </tr>
                    <tr className="bg-gray-25">
                      <td className="border border-gray-300 p-3 font-semibold">Application Logs</td>
                      <td className="border border-gray-300 p-3">System logs, error reports</td>
                      <td className="border border-gray-300 p-3">90 days from creation</td>
                      <td className="border border-gray-300 p-3">Troubleshooting and system maintenance</td>
                      <td className="border border-gray-300 p-3">Automatic deletion</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3 font-semibold">Analytics Data</td>
                      <td className="border border-gray-300 p-3">Anonymized usage statistics</td>
                      <td className="border border-gray-300 p-3">36 months from creation</td>
                      <td className="border border-gray-300 p-3">Service improvement and feature development</td>
                      <td className="border border-gray-300 p-3">Conversion to fully anonymized aggregate data after this period</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-black mt-8 mb-4 border-b border-gray-300 pb-2">How to complain</h2>
              <p className="mb-4">If you have any concerns about our use of your personal data, you can make a complaint to us using the contact details at the top of this privacy notice.</p>
              <p className="mb-6">If you remain unhappy with how we've used your data after raising a complaint with us, you can also complain to the ICO.</p>
              
              <p className="mb-4">The ICO's address:</p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <address className="not-italic">
                  Information Commissioner's Office<br />
                  Wycliffe House<br />
                  Water Lane<br />
                  Wilmslow<br />
                  Cheshire<br />
                  SK9 5AF
                </address>
              </div>
              
              <p className="mb-2"><strong>Helpline number:</strong> 0303 123 1113</p>
              <p className="mb-8"><strong>Website:</strong> <a href="https://www.ico.org.uk/make-a-complaint" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors">https://www.ico.org.uk/make-a-complaint</a></p>
              
              <div className="mt-8 pt-4 border-t border-gray-300">
                <p className="text-center"><strong>Last updated:</strong> 11th March 2025</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 